import React from 'react';
import { Button, Col, DatePicker, Form, Input, Popconfirm, Row, Select, Space, Switch, Upload } from 'antd';
import { openNotification } from '../../../utils';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { apiFetch } from 'services/apiClient';
import { createReportFormat, updateReportFormat, getReportFormat } from '../services/report-formatService';
import { CreateReportFormatDto } from '../types/ReportFormat';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import ImageService from 'services/ImageService';

export default function ReportFormatForm({ id, onSuccess, referenceId }: { id?: number; onSuccess: () => void; referenceId?: number }) {
  const [form] = Form.useForm<CreateReportFormatDto>();
  
  const [attributeOptions, setAttributeOptions] = React.useState<any[]>([]);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [logoSaving, setLogoSaving] = React.useState(false);
  const isAttributeFixed = !!referenceId;
  const normalizeNullableText = (value?: string | null) => {
    if (value == null) return null;
    return value.trim() === '' ? null : value;
  };
  React.useEffect(() => {
    if (id) {
      getReportFormat(id).then(d => {
        const data: any = { ...d };
        if (data.publishDate) data.publishDate = dayjs(data.publishDate);
        if (data.attribute) data.attribute = typeof data.attribute === 'object' ? data.attribute.id : data.attribute;
        data.isActive = Boolean(data.isActive);
        data.technicalManagerApproval = Boolean(data.technicalManagerApproval);
        data.isTested = Boolean(data.isTested);
        form.setFieldsValue(data);
        setLogoPreview(data.accreditationLogoUrl ?? null);
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isActive: false, technicalManagerApproval: false, isTested: false });
      if (isAttributeFixed) form.setFieldsValue({ attribute: referenceId });
      setLogoPreview(null);
    }
    if (!isAttributeFixed) {
      apiFetch('/attributes').then(setAttributeOptions);
    }
  }, [id, form, isAttributeFixed, referenceId]);

  const persistLogoChange = async (nextUrl: string | null, successMessage: string) => {
    if (!id) return;
    const values = form.getFieldsValue(true);
    const payload: any = { ...values, accreditationLogoUrl: nextUrl };
    payload.subTitle = normalizeNullableText(payload.subTitle);
    payload.bulkReportTableTitle = normalizeNullableText(payload.bulkReportTableTitle);
    if (isAttributeFixed) {
      payload.attribute = referenceId;
    }
    if (payload.publishDate) {
      payload.publishDate = dayjs(payload.publishDate).format('YYYY-MM-DD');
    }
    try {
      await updateReportFormat(id, payload);
      openNotification('success', 'Başarılı', successMessage);
    } catch (error) {
      throw error;
    }
  };

  const handleLogoUpload = async (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      openNotification('error', 'Hata', 'Yalnızca görsel dosyaları yükleyebilirsiniz.');
      return;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      openNotification('error', 'Hata', 'Logo dosyası 2 MB boyutunu aşamaz.');
      return;
    }
    const previousUrl = form.getFieldValue('accreditationLogoUrl');
    setLogoSaving(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const baseName = file.name.split('.').slice(0, -1).join('.') || 'logo';
      const uniqueName = `${baseName}_${Date.now()}`;
      const response = await ImageService.postImage(formData, uniqueName);
      const url = response?.data?.preSignUrl ?? response?.data?.url ?? response?.data;
      if (typeof url === 'string' && url.length > 0) {
        form.setFieldsValue({ accreditationLogoUrl: url });
        setLogoPreview(url);
        if (id) {
          await persistLogoChange(url, 'Logo güncellendi.');
        } else {
          openNotification('success', 'Başarılı', 'Logo yüklendi. Kaydetmeyi unutmayın.');
        }
      } else {
        form.setFieldsValue({ accreditationLogoUrl: previousUrl });
        setLogoPreview(previousUrl ? previousUrl : null);
        openNotification('error', 'Hata', 'Logo yükleme sonrasında geçerli bir URL elde edilemedi.');
      }
    } catch (error) {
      form.setFieldsValue({ accreditationLogoUrl: previousUrl });
      setLogoPreview(previousUrl ? previousUrl : null);
      const message = id ? 'Logo kaydedilirken bir hata oluştu.' : 'Logo yüklenirken bir hata oluştu.';
      openNotification('error', 'Hata', message);
    } finally {
      setLogoSaving(false);
    }
  };

  const handleLogoRemove = async () => {
    if (logoSaving) return;
    const previousUrl = form.getFieldValue('accreditationLogoUrl');
    setLogoSaving(true);
    form.setFieldsValue({ accreditationLogoUrl: null });
    setLogoPreview(null);
    try {
      if (id) {
        await persistLogoChange(null, 'Logo kaldırıldı.');
      } else {
        openNotification('success', 'Başarılı', 'Logo kaldırıldı. Kaydetmeyi unutmayın.');
      }
    } catch (error) {
      form.setFieldsValue({ accreditationLogoUrl: previousUrl });
      setLogoPreview(previousUrl ? previousUrl : null);
      openNotification('error', 'Hata', 'Logo kaldırılırken bir hata oluştu.');
    } finally {
      setLogoSaving(false);
    }
  };

  const handleLogoUrlInputChange = (value: string) => {
    const trimmed = value?.trim();
    setLogoPreview(trimmed ? trimmed : null);
  };

  const onFinish = async (values: CreateReportFormatDto) => {
    const data: any = { ...values };
    data.subTitle = normalizeNullableText(data.subTitle);
    data.bulkReportTableTitle = normalizeNullableText(data.bulkReportTableTitle);
    if (isAttributeFixed) data.attribute = referenceId;
    if (data.publishDate) data.publishDate = data.publishDate.format('YYYY-MM-DD');
    try {
      if (id) {
        await updateReportFormat(id, data);
        openNotification('success', 'Başarılı', 'Rapor Formatı Güncellendi');
      } else {
        await createReportFormat(data);
        openNotification('success', 'Başarılı', 'Rapor Formatı Eklendi');
      }
      onSuccess();
    } catch (e) {
      openNotification('error', 'Hata', 'Rapor Formatı işlemi başarısız');
    }
  };
  return (
    <>
      
      <Form form={form} onFinish={onFinish} layout='vertical'>
      <Row gutter={[16,16]}>
        {!isAttributeFixed && (
          <Col span={8}>
            <Form.Item name='attribute' label='Parametre' rules={[{ required: true }]}>
              <Select showSearch filterOption={(input, option) => ((option?.children?.toString()) || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))} placeholder='Seçiniz' style={{width:'100%'}}>
                {attributeOptions.map(o => <Select.Option key={o.id} value={o.id}>{o.name ?? o.id}</Select.Option>)}
              </Select>
            </Form.Item>
          </Col>
        )}
        <Col span={8}>
          <Form.Item name='title' label='Başlık' rules={[{ required: true }]}>
            <Input style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='subTitle' label='Alt Başlık'>
            <Input style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='version' label='Versiyon No' rules={[{ required: true }]}>
            <Input style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='publishDate' label='Yayın Tarihi' rules={[{ required: true }]}>
            <DatePicker format={APP_LOCAL_DATE_FORMAT} placeholder='Seçiniz' style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='documentNo' label='Doküman No' rules={[{ required: true }]}>
            <Input style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='note' label='Not' rules={[{ required: true }]}>
            <Input.TextArea rows={3} style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='bulkReportTableTitle' label='Toplu Rapor Tablo Başlığı'>
            <Input placeholder='Örn. Kompartıman' style={{width:'100%'}} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label='Akreditasyon Logosu'>
            <Space direction='vertical' style={{ width: '100%' }}>
              <Form.Item name='accreditationLogoUrl' noStyle>
                <Input placeholder='Logo URL girin veya yükleyin' onChange={(e) => handleLogoUrlInputChange(e.target.value)} />
              </Form.Item>
              <Upload
                accept='image/*'
                showUploadList={false}
                disabled={logoSaving}
                beforeUpload={async (file) => {
                  await handleLogoUpload(file as RcFile);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />} loading={logoSaving} disabled={logoSaving}>Logo Yükle</Button>
              </Upload>
              {logoPreview ? (
                <>
                  <img src={logoPreview} alt='Akreditasyon Logosu' style={{ maxWidth: '100%', maxHeight: 140, borderRadius: 4, border: '1px solid #f0f0f0', padding: 4 }} />
                  <Popconfirm
                    title='Logoyu kaldırmak istediğinize emin misiniz?'
                    okText='Evet'
                    cancelText='Hayır'
                    onConfirm={handleLogoRemove}
                    disabled={logoSaving}
                  >
                    <Button size='small' disabled={logoSaving}>Logoyu Kaldır</Button>
                  </Popconfirm>
                </>
              ) : (
                <div style={{ color: '#8c8c8c' }}>Logo eklemek için URL girin veya görsel yükleyin.</div>
              )}
            </Space>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='isActive' label='Aktif' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='technicalManagerApproval' label='Teknik Yönetici Onayı' valuePropName='checked'>
            <Switch checkedChildren='Evet' unCheckedChildren='Hayır' />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name='isTested' label='Test Edildi' valuePropName='checked'>
            <Switch checkedChildren='Evet' unCheckedChildren='Hayır' />
          </Form.Item>
        </Col>
      </Row>
      <Button htmlType='submit' type='primary' size='small' className='btn-inline'>Kaydet</Button>
    </Form>
    </>
  );
}
